import { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { Permission, Role } from '@/services/roles.service';

const resourceMap: Record<string, string> = {
  sales: 'Ventas',
  inventory: 'Inventario',
  products: 'Productos',
  customers: 'Clientes',
  cash_register: 'Caja',
  suppliers: 'Proveedores',
  purchases: 'Compras',
  users: 'Usuarios',
  roles: 'Roles y Permisos',
  companies: 'Empresas',
  branches: 'Sucursales',
  reports: 'Reportes',
  settings: 'Configuración',
  system: 'Sistema',
  backups: 'Respaldos',
  brands: 'Marcas',
  categories: 'Categorías',
  commissions: 'Comisiones',
  invoicing: 'Facturación',
  promotions: 'Promociones',
};

const actionMap: Record<string, string> = {
  create: 'Crear',
  read: 'Leer / Ver',
  update: 'Editar / Actualizar',
  delete: 'Eliminar',
  cancel: 'Cancelar',
  export: 'Exportar',
};

interface RoleFormModalProps {
  role: Role | null;
  permissions: Permission[];
  onClose: () => void;
  onSave: (data: { name: string; description: string; permissionIds: string[] }) => Promise<void>;
  saving: boolean;
}

export function RoleFormModal({ role, permissions, onClose, onSave, saving }: RoleFormModalProps) {
  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (role && role.rolePermissions) {
      setSelectedPerms(new Set(role.rolePermissions.map((rp) => rp.permission.id)));
    } else {
      setSelectedPerms(new Set());
    }
  }, [role]);

  // Group permissions by resource
  const groupedPerms = permissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) acc[perm.resource] = [];
    acc[perm.resource].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  const togglePermission = (id: string) => {
    const newSet = new Set(selectedPerms);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedPerms(newSet);
  };

  const handleSave = () => {
    onSave({
      name,
      description,
      permissionIds: Array.from(selectedPerms),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] w-full max-w-4xl shadow-ios-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-ios-gray-5 sticky top-0 bg-white z-10 rounded-t-[20px]">
          <h3 className="font-semibold">{role ? 'Editar Rol' : 'Nuevo Rol'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-ios-gray-6">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-xs text-ios-gray mb-1 block">Nombre del Rol *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={role?.name === 'SUPER_ADMIN'}
                className="ios-input w-full uppercase"
                placeholder="Ej. GERENTE_SUCURSAL"
              />
              {role?.name === 'SUPER_ADMIN' && (
                <p className="text-[10px] text-orange-500 mt-1">Este rol es del sistema y su nombre no puede modificarse.</p>
              )}
            </div>
            <div>
              <label className="text-xs text-ios-gray mb-1 block">Descripción</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="ios-input w-full"
                placeholder="Encargado de la sucursal..."
              />
            </div>
          </div>

          <h4 className="font-medium text-sm mb-3">Permisos del Sistema</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(groupedPerms).map(([resource, perms]) => (
              <div key={resource} className="border border-ios-gray-5 rounded-xl p-3 bg-ios-gray-6/20">
                <h5 className="text-xs font-semibold uppercase text-ios-gray mb-2 border-b border-ios-gray-5 pb-1">
                  {resourceMap[resource] || resource}
                </h5>
                <div className="space-y-2">
                  {perms.map((perm) => (
                    <label
                      key={perm.id}
                      onClick={(e) => {
                        e.preventDefault();
                        togglePermission(perm.id);
                      }}
                      className={`flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded-lg transition-colors ${
                        selectedPerms.has(perm.id) ? 'bg-ios-blue/10 text-ios-blue' : 'hover:bg-ios-gray-6'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        selectedPerms.has(perm.id) ? 'bg-ios-blue border-ios-blue text-white' : 'border-ios-gray-4 bg-white'
                      }`}>
                        {selectedPerms.has(perm.id) && <Check size={12} />}
                      </div>
                      <span className="capitalize">{actionMap[perm.action] || perm.action}</span>
                      {perm.description && (
                        <span className="text-[10px] text-ios-gray ml-auto" title={perm.description}>
                          info
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-ios-gray-5 flex gap-2 rounded-b-[20px] bg-white">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || role?.name === 'SUPER_ADMIN'}
            className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Guardando...' : role ? 'Actualizar Permisos' : 'Crear Rol'}
          </button>
        </div>
      </div>
    </div>
  );
}
